import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Badge,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { Check, Loader2, Tags } from "lucide-react"
import type { ClientUserListItem, ClientUserTag } from "@/hooks/api"
import { DEFAULT_TAG_COLOR } from "./clientUserTagColors"

export interface ClientUserTagInlineEditorProps {
  user: ClientUserListItem
  tags: ClientUserTag[]
  canManage: boolean
  saving: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onAddTag: (user: ClientUserListItem, tagId: string) => void
  onRemoveTag: (user: ClientUserListItem, tagId: string) => void
}

export const ClientUserTagInlineEditor = ({
  user,
  tags,
  canManage,
  saving,
  open: controlledOpen,
  onOpenChange,
  onAddTag,
  onRemoveTag,
}: ClientUserTagInlineEditorProps) => {
  const { t } = useTranslation()
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen
  const keepOpenOnNextCloseRef = useRef(false)
  const assignedTagIds = useMemo(
    () => new Set(user.tags.map((tag) => tag.id)),
    [user.tags]
  )

  const tagBadges =
    user.tags.length > 0 ? (
      user.tags.map((tag) => (
        <Badge
          key={tag.id}
          variant="outline"
          className="max-w-28 gap-1.5 border-slate-200 bg-background font-normal md:max-w-32"
        >
          <span
            className="h-2 w-2 shrink-0 rounded-full border"
            style={{
              backgroundColor: tag.color || DEFAULT_TAG_COLOR,
              borderColor: tag.color || DEFAULT_TAG_COLOR,
            }}
          />
          <span className="truncate">{tag.name}</span>
        </Badge>
      ))
    ) : (
      <span className="text-muted-foreground">-</span>
    )

  const keepOpenAfterSelect = (action: () => void) => {
    keepOpenOnNextCloseRef.current = true
    action()
    setOpen(true)
    window.setTimeout(() => {
      keepOpenOnNextCloseRef.current = false
    }, 0)
  }

  if (!canManage) {
    return (
      <div className="flex max-w-[220px] flex-wrap items-center gap-1.5 md:max-w-[240px]">
        {tagBadges}
      </div>
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && keepOpenOnNextCloseRef.current) {
          keepOpenOnNextCloseRef.current = false
          return
        }
        setOpen(nextOpen)
      }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="group/tag-editor relative flex max-w-[220px] flex-wrap items-center gap-1.5 rounded-md text-left outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 md:max-w-[240px]"
                disabled={saving}
                aria-label={t("crm.userManagement.tags.assign")}
              >
                {tagBadges}
                <span className="absolute -right-7 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-[color,opacity] group-hover:opacity-100 group-focus-visible/tag-editor:opacity-100 group-data-[state=open]/tag-editor:opacity-100 group-hover/tag-editor:text-foreground">
                  {saving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Tags className="h-3.5 w-3.5" />
                  )}
                </span>
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{t("crm.userManagement.tags.assign")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={8}
        className="w-72 overflow-hidden p-0"
        onClick={(e) => e.stopPropagation()}
      >
        {tags.length > 0 ? (
          <Command>
            <CommandInput placeholder={t("crm.userManagement.tags.search")} />
            <CommandList className="h-60">
              <CommandEmpty>
                {t("crm.userManagement.tags.noSearchResults")}
              </CommandEmpty>
              <CommandGroup>
                {tags.map((tag) => {
                  const selected = assignedTagIds.has(tag.id)
                  return (
                    <CommandItem
                      key={tag.id}
                      value={`${tag.name} ${tag.id}`}
                      disabled={saving}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                      onSelect={() => {
                        keepOpenAfterSelect(() => {
                          if (selected) {
                            onRemoveTag(user, tag.id)
                          } else {
                            onAddTag(user, tag.id)
                          }
                        })
                      }}
                    >
                      <span
                        className="h-2 w-2 shrink-0 rounded-full border"
                        style={{
                          backgroundColor: tag.color || DEFAULT_TAG_COLOR,
                          borderColor: tag.color || DEFAULT_TAG_COLOR,
                        }}
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {tag.name}
                      </span>
                      {selected && <Check className="h-4 w-4" />}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-4 text-sm text-muted-foreground">
            {t("crm.userManagement.tags.assignEmpty")}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
